import { Step, Stepper } from "@/design-system/salt";

/**
 * WizardStepper — the one step rail for this app, backed by Salt's Stepper pattern.
 *
 * This app was maintaining TWELVE different multi-step shapes and exactly one of
 * them was Salt's: a `Progress` bar plus a "Step N of M" sentence; an `<ol>` whose
 * text is the literal `3/4: Review`; Salt `Tabs` with unreached tabs disabled;
 * `NavigationTabBar` with `currentType="step"`; hand-drawn 24px circles with a
 * chevron shoved between them; a vertical rail of ghost Buttons with 6px dots at
 * `left: -19px` faking a connector that is never drawn; a bare `<span>` row
 * recoloured by index. One flow had no indicator at all.
 *
 * They accumulated because there was no rule to violate — see DR-29.
 *
 * Salt owns the circle, the connector, the completed icon, the typography and the
 * list/step ARIA. This maps a step model onto Salt's stages and nothing more.
 *
 * ## What this deliberately does NOT do
 *
 * Salt's `Step` is a non-interactive `<li>`. Its only callback is `onToggle`,
 * typed `ButtonProps["onClick"]`, which drives the nested-step expand chevron and
 * is NOT a step-select handler. Its label is rendered `aria-hidden`, so the
 * obvious escape hatch — passing a `<Button>` as the label to keep click-to-jump —
 * is an accessibility violation rather than a workaround.
 *
 * So a rail whose steps are clickable today LOSES click-to-jump when it converts.
 * That is a product decision, not a refactor detail, and it is called out per flow
 * in docs/audit/WIZARD-INVENTORY.md rather than decided here.
 *
 * @see https://storybook.saltdesignsystem.com/?path=/story/patterns-wizard--horizontal
 */

/** Salt's own stage vocabulary, re-exported so callers need not reach past the facade. */
export type WizardStepStage = "pending" | "locked" | "completed" | "inprogress" | "active";

export interface WizardStepDefinition {
  /**
   * Stable key. The previous version keyed on `label`, which silently breaks when
   * two steps share a name — real in this app, where the merchant package repeats
   * "Business" across products.
   */
  id: string;
  label: string;
  /** Shown under the label. Used for AP upload counts and per-step prompts. */
  description?: string;
  /** Surfaces validation state on the rail itself. */
  status?: "warning" | "error";
  /** Explicit override; beats the stage derived from `currentStep`. */
  stage?: WizardStepStage;
  /** One level only — Salt warns above depth 2 and rejects nesting when horizontal. */
  steps?: WizardStepDefinition[];
  /**
   * `data-*` attributes for this step's `<li>`. Salt's `Step` extends
   * `ComponentPropsWithoutRef<"li">`, so they reach the DOM.
   *
   * Exists because converting a rail must not silently drop the hooks its tests
   * and analytics already depend on — the portal handoff strip asserts
   * `data-testid="portal-section-*"` and `data-complete`, and a conversion that
   * quietly deleted them would look green while removing real coverage.
   */
  data?: Record<string, string>;
}

/**
 * Where a step's `completed` state comes from. See DR-31.
 *
 * - `"validated"` — a step is `completed` ONLY when it says so, via `step.stage`.
 *   The rail then reports work that was actually done.
 * - `"position"` — every step before the cursor is painted `completed`. This
 *   certifies work that MAY NOT HAVE HAPPENED, and is tracked technical debt.
 */
export type WizardCompletionSource = "validated" | "position";

export interface WizardStepperProps {
  steps: WizardStepDefinition[];
  /** 1-based index of the active step. */
  currentStep: number;
  /**
   * REQUIRED, and deliberately without a default.
   *
   * `completed` used to be derived from position alone, so anything that moved the
   * cursor — a URL parameter, a rail click, an ungated Continue — retroactively
   * certified every step behind it. MEASURED consequences of that:
   *   · a blank supplier onboarding package reported straight-through processing
   *     "complete" and 17% progress before the user typed a character;
   *   · editing `?step=` painted 7 of 8 steps completed on a session with 0
   *     suppliers assigned;
   *   · an untouched treasury step was stamped completed by an ungated Continue.
   *
   * In a banking application, pre-certifying an underwriting-adjacent section is
   * not a cosmetic bug. There is no default here precisely so that every caller
   * has to state which guarantee it can actually make, and so the unsafe answer
   * is greppable rather than invisible.
   */
  completionSource: WizardCompletionSource;
  /** Vertical suits many steps or long step bodies. Horizontal is the default. */
  orientation?: "horizontal" | "vertical";
  /** Names the rail for assistive tech; say what the wizard is for. */
  "aria-label"?: string;
}

/**
 * Resolve a step's stage.
 *
 * An explicit `step.stage` always wins — that is how a flow reports real validation
 * state, including Salt's `"locked"` for a step whose prerequisites are unmet.
 */
function stageFor(
  step: WizardStepDefinition,
  index: number,
  currentStep: number,
  completionSource: WizardCompletionSource,
): WizardStepStage {
  if (step.stage) return step.stage;
  const position = index + 1;
  if (position === currentStep) return "active";
  // Behind the cursor. "You walked past this" and "this is done" are DIFFERENT
  // facts; only `completionSource="position"` conflates them, and only on request.
  if (position < currentStep) return completionSource === "position" ? "completed" : "pending";
  return "pending";
}

export function WizardStepper({
  steps,
  currentStep,
  completionSource,
  orientation = "horizontal",
  "aria-label": ariaLabel = "Wizard progress",
}: WizardStepperProps) {
  const nested = steps.some((step) => step.steps?.length);
  // Salt logs a runtime warning and drops nested steps in a horizontal Stepper.
  // Fail loudly in development instead of silently rendering a different rail.
  if (nested && orientation === "horizontal" && import.meta.env.DEV) {
    throw new Error(
      'WizardStepper: nested steps require orientation="vertical" — Salt\'s horizontal Stepper does not support them.',
    );
  }

  return (
    /*
     * The wrapper is load-bearing, not decoration.
     *
     * Salt's Stepper CSS sets `grid-area: stepper` on the rail itself
     * (@salt-ds/core/dist-cjs/stepper/Stepper.css.js). That is fine inside Salt's
     * own StepperLayout, which defines a line with that name — and destructive
     * anywhere else. Drop a bare Stepper into any CSS grid that has no `stepper`
     * line and the browser invents implicit named lines and implicit tracks:
     * MEASURED on /conversation-guide/new at 1440x900, the parent computed
     * `grid-template-columns: 192px 214.125px 0px 385.875px` instead of the
     * authored `minmax(12rem,16rem) minmax(0,1fr)`, the rail landed at x=891
     * y=1725 on a 2189px page — below the fold, invisible on first paint — and
     * the form panel beside it was crushed to 192px wide.
     *
     * The wrapper becomes the grid item, so `grid-area: stepper` resolves inside
     * a plain block and affects nothing. Fixing it here rather than at the call
     * site means the next flow that puts a rail in a GridLayout cannot rediscover
     * this: the failure is invisible to jsdom and to every gate we run.
     */
    <div
      /*
       * R-48: keep the rail out of the sequential tab order.
       *
       * Chromium makes a container focusable when its content overflows its box,
       * so this wrapper became the FIRST tab stop in every wizard — a keyboard
       * user landed a focus ring on a decorative rail before reaching a single
       * control. MEASURED in both reference wizards; axe reports 0 violations for
       * it, because a focusable scroll container is legitimate in general.
       *
       * Salt's `Step` is a non-interactive `<li>` and this rail has nothing to
       * operate, so `-1` costs no functionality: the list is still exposed to
       * assistive tech by its list semantics and `aria-label`, and is still
       * reachable programmatically.
       */
      tabIndex={-1}
    >
      <Stepper aria-label={ariaLabel} orientation={orientation}>
        {steps.map((step, index) => (
          <Step
            key={step.id}
            label={step.label}
            description={step.description}
            status={step.status}
            stage={stageFor(step, index, currentStep, completionSource)}
            {...step.data}
          >
            {step.steps?.map((child, childIndex) => (
              <Step
                key={child.id}
                label={child.label}
                description={child.description}
                status={child.status}
                stage={stageFor(child, childIndex, currentStep, completionSource)}
                {...child.data}
              />
            ))}
          </Step>
        ))}
      </Stepper>
    </div>
  );
}
